import { Badge, Button } from '@fsd/ui';
import { ArrowRight, Award, CheckCircle2, Shield, Sprout, Target, Zap } from 'lucide-react';
import { useTranslation } from '@/i18n/hooks';
import { scrollToSection, useInView } from '@/utils';

export function Spraying() {
  const { t } = useTranslation('home');

  const [sectionRef, isSectionInView] = useInView({ threshold: 0.1 });
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.2 });

  const benefits = [
    { key: 'spraying.benefits.precision' as const, icon: Target },
    { key: 'spraying.benefits.efficiency' as const, icon: Zap },
    { key: 'spraying.benefits.safety' as const, icon: Shield },
    { key: 'spraying.benefits.technology' as const, icon: Award },
  ];

  const services = [
    { key: 'spraying.services.mapping' as const },
    { key: 'spraying.services.calibration' as const },
    { key: 'spraying.services.application' as const },
    { key: 'spraying.services.support' as const },
  ];

  const handleContact = () => {
    scrollToSection('#contact');
  };

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="maintenance"
      className="relative overflow-hidden bg-white py-16 sm:py-20 lg:py-32 dark:bg-slate-900"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,#1e293b08_1px,transparent_1px),linear-gradient(-45deg,#64748b08_1px,transparent_1px)] bg-size-[40px_40px]"></div>
      </div>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-16 right-0 h-64 w-64 rounded-full bg-primary/10 blur-3xl animate-pulse sm:top-32 sm:right-32"></div>
        <div className="absolute -bottom-16 left-0 h-80 w-80 rounded-full bg-primary/5 blur-3xl animate-pulse delay-1000 sm:bottom-32 sm:left-32"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="mb-12 text-center lg:mb-16"
        >
          <Badge
            variant="primary"
            size="lg"
            icon={<Sprout className="h-4 w-4" />}
            className={`mb-6 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
          >
            {t('spraying.badge')}
          </Badge>

          <h2
            className={`mb-4 text-3xl font-bold text-slate-900 sm:text-4xl lg:text-6xl dark:text-slate-100 slide-in-left-ready ${isHeaderInView ? 'in-view' : ''}`}
          >
            <span className="block">{t('spraying.title.part1')}</span>
            <span className="block text-primary" style={{ transitionDelay: '200ms' }}>
              {t('spraying.title.part2')}
            </span>
          </h2>
          <p
            className={`mx-auto max-w-3xl text-lg text-slate-600 dark:text-slate-400 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
            style={{ transitionDelay: '300ms' }}
          >
            {t('spraying.subtitle')}
          </p>
        </div>

        {/* Main Content */}
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left Column - Description and Benefits */}
          <div className="space-y-8">
            {/* Description */}
            <div
              className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '400ms' }}
            >
              <div className="space-y-4 text-base leading-relaxed text-slate-600 sm:text-lg dark:text-slate-300">
                <p>{t('spraying.description.paragraph1')}</p>
                <p>{t('spraying.description.paragraph2')}</p>
                <p>{t('spraying.description.paragraph3')}</p>
              </div>
            </div>

            {/* Benefits */}
            <div
              className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '500ms' }}
            >
              <h3 className="mb-6 text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {t('spraying.benefits.title')}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {benefits.map((benefit, index) => {
                  const IconComponent = benefit.icon;
                  return (
                    <div
                      key={benefit.key}
                      className={`group flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-300 hover:border-primary/50 hover:shadow-md dark:border-slate-700 dark:bg-slate-800 fade-in-ready ${isSectionInView ? 'in-view' : ''}`}
                      style={{ transitionDelay: `${600 + index * 100}ms` }}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className="mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {t(benefit.key)}
                        </h4>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column - Services and Equipment */}
          <div className="space-y-8">
            {/* Services */}
            <div
              className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '700ms' }}
            >
              <h3 className="mb-6 text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {t('spraying.services.title')}
              </h3>
              <div className="space-y-3">
                {services.map((service, index) => (
                  <div
                    key={service.key}
                    className={`flex items-start gap-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-800 fade-in-ready ${isSectionInView ? 'in-view' : ''}`}
                    style={{ transitionDelay: `${800 + index * 100}ms` }}
                  >
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <p className="text-sm text-slate-700 dark:text-slate-300">{t(service.key)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Equipment */}
            <div
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800 fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '1000ms' }}
            >
              <h3 className="mb-4 text-xl font-semibold text-slate-900 dark:text-slate-100">
                {t('spraying.equipment.title')}
              </h3>
              <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                {t('spraying.equipment.description')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="primary" size="sm">
                  XAG P100 Pro
                </Badge>
                <Badge variant="primary" size="sm">
                  RTK GPS
                </Badge>
                <Badge variant="primary" size="sm">
                  {t('spraying.equipment.certified')}
                </Badge>
              </div>
            </div>

            {/* CTA */}
            <div
              className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '1100ms' }}
            >
              <Button size="lg" onClick={handleContact} className="w-full group">
                {t('spraying.cta.button')}
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <p className="mt-3 text-center text-sm text-slate-600 dark:text-slate-400">
                {t('spraying.cta.description')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
